import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, IconButton, Paper, Typography, Chip, Fade, Tooltip, Drawer, ButtonGroup, Button, CircularProgress, Alert, Switch, FormControlLabel } from '@mui/material';
import { MyLocation, Layers, FilterList, DirectionsCar, Speed, Close, Refresh, LightMode, DarkMode, Shield } from '@mui/icons-material';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useFleetTracking } from '../hooks/useFleetTracking';
import { Device } from '../types';
import api from '../services/api';
import { onFlatMapStyleReady } from '../utils/mapStyle';

interface GeofenceZone {
  _id: string;
  name: string;
  type: 'circular' | 'polygon';
  center?: { type: string; coordinates: [number, number] };
  radius?: number;
  polygon?: { type: string; coordinates: Array<Array<[number, number]>> };
  color?: string;
  isActive: boolean;
}

// ── Cluster layer constants ────────────────────────────────────────────────────
const CLUSTER_SOURCE = 'fleet-cluster-source';
const LAYER_CLUSTERS = 'fleet-clusters';
const LAYER_COUNT = 'fleet-cluster-count';

const MinimalFleetMap: React.FC = () => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<{ [imei: string]: mapboxgl.Marker }>({});
  const clusterSourceReadyRef = useRef(false);

  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [colorMode, setColorMode] = useState<'status' | 'speed'>('status');
  const [showOffline, setShowOffline] = useState(true);
  const [showGeofences, setShowGeofences] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapStyle, setMapStyle] = useState<'light' | 'dark'>('dark');
  const [vehicleData, setVehicleData] = useState<{ [imei: string]: any }>({});
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const [addresses, setAddresses] = useState<{ [key: string]: string }>({});
  const [lastKnownSpeeds, setLastKnownSpeeds] = useState<{ [imei: string]: number }>({});
  const [geofenceZones, setGeofenceZones] = useState<{ [vehicleId: string]: GeofenceZone[] }>({});
  const geofenceLayersRef = useRef<Set<string>>(new Set());

  const { fleetDevices, isConnected, isSubscribed, stats, error, reconnect, clearError } = useFleetTracking();

  // ✅ Universal telemetry parsers for BOTH JTT808 and Teltonika protocols
  const parseIgnition = useCallback((tel: any): boolean => {
    if (tel?.acc_on !== undefined) {
      return tel.acc_on === true;
    }
    if (tel?.state?.reported?.['239'] !== undefined) {
      return tel.state.reported['239'] === 1;
    }
    return false;
  }, []);

  const toNum = (v: any): number | null => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const parseLocation = useCallback((tel: any): { lat: number | null; lng: number | null } => {
    const lat1 = toNum(tel?.location?.latitude);
    const lng1 = toNum(tel?.location?.longitude);
    if (lat1 !== null && lng1 !== null) return { lat: lat1, lng: lng1 };
    const lat2 = toNum(tel?.latitude);
    const lng2 = toNum(tel?.longitude);
    if (lat2 !== null && lng2 !== null) return { lat: lat2, lng: lng2 };
    return { lat: null, lng: null };
  }, []);

  const parseSpeed = useCallback((tel: any): number => {
    if (tel?.speed_kmh !== undefined) return tel.speed_kmh;
    if (tel?.instantaneous_speed_kmh !== undefined) return tel.instantaneous_speed_kmh;
    if (tel?.state?.reported?.sp !== undefined) return tel.state.reported.sp;
    return 0;
  }, []);

  // Fetch vehicle data for ALL bound devices
  useEffect(() => {
    const fetchAllVehicleData = async () => {
      try {
        const newVehicleData = { ...vehicleData };
        const devicesNeedingFetch: Array<{ device: Device }> = [];

        Array.from(fleetDevices.values()).forEach(({ device }) => {
          if (!device.isBoundToVehicle || !device.vehicleId) return;
          if (vehicleData[device.imei]) return;
          if (typeof device.vehicleId === 'object' && device.vehicleId.plateNumber) {
            console.log(`✅ Using populated vehicle data for device ${device.imei}`);
            newVehicleData[device.imei] = {
              plateNumber: device.vehicleId.plateNumber,
              make: device.vehicleId.make,
              model: device.vehicleId.model,
              year: device.vehicleId.year,
              vin: device.vehicleId.vin
            };
          } else if (typeof device.vehicleId === 'string') {
            devicesNeedingFetch.push({ device });
          }
        });

        const hasNewData = Object.keys(newVehicleData).length > Object.keys(vehicleData).length;
        if (hasNewData) setVehicleData(newVehicleData);

        if (devicesNeedingFetch.length > 0) {
          console.log(`🚗 Fetching vehicle data for ${devicesNeedingFetch.length} devices...`);

          const fetchPromises = devicesNeedingFetch.map(async ({ device }) => {
            try {
              const vehicleId = typeof device.vehicleId === 'string'
                ? device.vehicleId
                : device.vehicleId?._id || device.vehicleId;
              if (!vehicleId) {
                console.error(`❌ Invalid vehicleId for device ${device.imei}`);
                return null;
              }
              const response = await api.get(`/vehicles/${vehicleId}`);
              const vehicle = response.data.data;
              return {
                imei: device.imei,
                vehicle: { plateNumber: vehicle.plateNumber, make: vehicle.make, model: vehicle.model, year: vehicle.year, vin: vehicle.vin }
              };
            } catch (error) {
              console.error(`❌ Error fetching vehicle for ${device.imei}:`, error);
              return null;
            }
          });

          const results = await Promise.all(fetchPromises);
          results.forEach(result => { if (result) newVehicleData[result.imei] = result.vehicle; });
          setVehicleData(newVehicleData);
          console.log(`✅ Fetched vehicle data for ${results.filter(r => r).length} devices`);
        }
      } catch (error) {
        console.error('❌ Error fetching vehicle data:', error);
      }
    };
    fetchAllVehicleData();
  }, [fleetDevices]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reverse geocoding function
  const reverseGeocode = useCallback(async (lat: number, lng: number): Promise<string> => {
    const cacheKey = `${lat.toFixed(4)},${lng.toFixed(4)}`;
    if (addresses[cacheKey]) return addresses[cacheKey];
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxgl.accessToken}`
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        const address = data.features[0].place_name;
        setAddresses(prev => ({ ...prev, [cacheKey]: address }));
        return address;
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  }, [addresses]);

  // Track last known speeds
  useEffect(() => {
    fleetDevices.forEach(({ device, telemetry }) => {
      const speed = parseSpeed(telemetry);
      if (telemetry && speed > 0) {
        setLastKnownSpeeds(prev => ({ ...prev, [device.imei]: speed }));
      }
    });
  }, [fleetDevices, parseSpeed]);

  // ── NEW: Add GeoJSON cluster source + layers ───────────────────────────────
  const _addClusterLayers = useCallback((map: mapboxgl.Map) => {
    if (map.getSource(CLUSTER_SOURCE)) return;

    map.addSource(CLUSTER_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
    });

    map.addLayer({
      id: LAYER_CLUSTERS,
      type: 'circle',
      source: CLUSTER_SOURCE,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': ['step', ['get', 'point_count'], '#4caf50', 5, '#ff9800', 10, '#f44336'],
        'circle-radius': ['step', ['get', 'point_count'], 22, 5, 28, 10, 36],
        'circle-opacity': 0.9,
        'circle-stroke-width': 3,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-opacity': 0.6,
      },
    });

    map.addLayer({
      id: LAYER_COUNT,
      type: 'symbol',
      source: CLUSTER_SOURCE,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 14,
      },
      paint: { 'text-color': '#ffffff' },
    });

    map.on('click', LAYER_CLUSTERS, (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [LAYER_CLUSTERS] });
      if (!features.length) return;
      const clusterId = features[0].properties?.cluster_id;
      (map.getSource(CLUSTER_SOURCE) as mapboxgl.GeoJSONSource).getClusterExpansionZoom(
        clusterId,
        (err, zoom) => {
          if (err) return;
          map.easeTo({
            center: (features[0].geometry as GeoJSON.Point).coordinates as [number, number],
            zoom: zoom ?? 14,
            duration: 500,
          });
        }
      );
    });

    map.on('mouseenter', LAYER_CLUSTERS, () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', LAYER_CLUSTERS, () => { map.getCanvas().style.cursor = ''; });

    clusterSourceReadyRef.current = true;
    console.log('✅ Cluster source + layers added');
  }, []);

  // ── NEW: Push current device positions into GeoJSON cluster source ─────────
  const _updateClusterSource = useCallback(() => {
    if (!mapRef.current || !clusterSourceReadyRef.current) return;
    const source = mapRef.current.getSource(CLUSTER_SOURCE) as mapboxgl.GeoJSONSource;
    if (!source) return;

    const features: GeoJSON.Feature[] = [];

    Array.from(fleetDevices.values())
      .filter(({ device }) => device.isBoundToVehicle)
      .filter(({ device }) => showOffline || device.status !== 'offline')
      .forEach(({ device, telemetry }) => {
        let lng: number | undefined;
        let lat: number | undefined;

        if (telemetry?.location?.latitude && telemetry?.location?.longitude) {
          lng = telemetry.location.longitude;
          lat = telemetry.location.latitude;
        } else {
          const coords = device.location?.coordinates;
          if (coords && coords[0] !== 0 && coords[1] !== 0) [lng, lat] = coords;
        }

        if (!lng || !lat || lng < -180 || lng > 180 || lat < -90 || lat > 90) return;

        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: { imei: device.imei },
        });
      });

    source.setData({ type: 'FeatureCollection', features });
  }, [fleetDevices, showOffline]);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1Ijoic29waGllMDEiLCJhIjoiY21jbmZtaHBkMDNhMjJuczMxd3dmeWVobCJ9.7W1F9G-KWZRqsiNPEle4sA';

    console.log('Initializing Mapbox map');

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [3.3792, 6.5244],
      zoom: 12,
      attributionControl: false
    });

    mapRef.current.addControl(new mapboxgl.NavigationControl(), 'bottom-right');

    const map = mapRef.current;
    const removeStyleListener = onFlatMapStyleReady(map, () => {
      if (mapRef.current !== map) return;
      clusterSourceReadyRef.current = false;
      geofenceLayersRef.current.clear();
      _addClusterLayers(map);
      setMapLoaded(true);
    });

    return () => {
      removeStyleListener();
      Object.values(markersRef.current).forEach(m => m.remove());
      mapRef.current?.remove();
      mapRef.current = null;
      clusterSourceReadyRef.current = false;
    };
  }, [_addClusterLayers]);

  // Fetch and display geofence zones
  const fetchAndDisplayGeofenceZones = useCallback(async () => {
    if (!mapRef.current || !mapLoaded) return;

    console.log('🛡️ Fetching geofence zones for all vehicles...');

    try {
      geofenceLayersRef.current.forEach(layerId => {
        if (mapRef.current?.getLayer(layerId)) mapRef.current.removeLayer(layerId);
        if (mapRef.current?.getSource(layerId)) mapRef.current.removeSource(layerId);
      });
      geofenceLayersRef.current.clear();

      const allZones: { [vehicleId: string]: GeofenceZone[] } = {};

      console.log('📋 Fetching all vehicles...');
      const allVehiclesResponse = await api.get('/vehicles');
      const allVehicles = allVehiclesResponse.data.data || [];

      console.log(`✅ Found ${allVehicles.length} total vehicles`);

      for (const vehicle of allVehicles) {
        try {
          if (!vehicle.geofence?.enabled) {
            console.log(`⏭️ Skipping ${vehicle.plateNumber} - geofencing not enabled`);
            continue;
          }

          const zonesResponse = await api.get(`/geofencing/vehicles/${vehicle._id}/zones`);
          const zones = zonesResponse.data.data.zones || [];

          if (zones.length > 0) {
            allZones[vehicle._id] = zones;
            console.log(`✅ Found ${zones.length} zones for ${vehicle.plateNumber}`);
          }

          zones.forEach((zone: GeofenceZone, zoneIndex: number) => {
            const uniqueId = `${vehicle._id}-${zone._id || zoneIndex}`;
            const layerId = `geofence-${uniqueId}`;
            const labelLayerId = `geofence-label-${uniqueId}`;
            const zoneColor = zone.color || '#3b82f6';

            console.log(`🎨 Rendering zone: ${zone.name} (${zone.type}) for ${vehicle.plateNumber}`);

            if (zone.type === 'circular' && zone.center && zone.radius) {
              const sourceId = layerId;
              const center = zone.center.coordinates;
              const radiusInKm = zone.radius / 1000;
              const points = 64;
              const coordinates: [number, number][] = [];

              for (let i = 0; i <= points; i++) {
                const angle = (i * 360) / points;
                const dx = radiusInKm * Math.cos((angle * Math.PI) / 180);
                const dy = radiusInKm * Math.sin((angle * Math.PI) / 180);
                const lat = center[1] + (dy / 111.32);
                const lng = center[0] + (dx / (111.32 * Math.cos((center[1] * Math.PI) / 180)));
                coordinates.push([lng, lat]);
              }

              mapRef.current!.addSource(sourceId, {
                type: 'geojson',
                data: {
                  type: 'Feature',
                  properties: { name: zone.name, color: zoneColor },
                  geometry: { type: 'Polygon', coordinates: [coordinates] }
                }
              });

              mapRef.current!.addLayer({
                id: layerId,
                type: 'fill',
                source: sourceId,
                paint: { 'fill-color': zoneColor, 'fill-opacity': zone.isActive ? 0.25 : 0.08 }
              });

              mapRef.current!.addLayer({
                id: `${layerId}-border`,
                type: 'line',
                source: sourceId,
                paint: {
                  'line-color': zoneColor,
                  'line-width': zone.isActive ? 4 : 2,
                  'line-dasharray': zone.isActive ? [1, 0] : [2, 2],
                  'line-opacity': zone.isActive ? 1 : 0.6,
                  'line-blur': zone.isActive ? 0.5 : 0
                }
              });

              mapRef.current!.addLayer({
                id: labelLayerId,
                type: 'symbol',
                source: sourceId,
                layout: {
                  'text-field': `🛡️ ${zone.name}`,
                  'text-size': 14,
                  'text-anchor': 'center',
                  'text-offset': [0, 0]
                },
                paint: {
                  'text-color': zoneColor,
                  'text-halo-color': mapStyle === 'dark' ? '#000000' : '#ffffff',
                  'text-halo-width': 3,
                  'text-halo-blur': 1
                }
              });

              geofenceLayersRef.current.add(layerId);
              geofenceLayersRef.current.add(`${layerId}-border`);
              geofenceLayersRef.current.add(labelLayerId);

              mapRef.current!.on('mouseenter', layerId, () => {
                mapRef.current!.getCanvas().style.cursor = 'pointer';
              });
              mapRef.current!.on('mouseleave', layerId, () => {
                mapRef.current!.getCanvas().style.cursor = '';
              });
              mapRef.current!.on('click', layerId, () => {
                new mapboxgl.Popup()
                  .setLngLat(center as [number, number])
                  .setHTML(`
                    <div style="padding: 14px; font-family: system-ui, -apple-system, sans-serif; background: ${mapStyle === 'dark' ? '#1e1e1e' : '#ffffff'}; color: ${mapStyle === 'dark' ? '#fff' : '#000'};">
                      <h3 style="margin: 0 0 10px 0; color: ${zoneColor}; font-size: 16px; font-weight: bold;">
                        🛡️ ${zone.name}
                      </h3>
                      <div style="font-size: 13px; line-height: 1.8;">
                        <div><strong>Type:</strong> Circular</div>
                        <div><strong>Radius:</strong> ${zone.radius}m</div>
                        <div>
                          <strong>Status:</strong>
                          ${zone.isActive
                      ? '<span style="color: #4caf50;">● Active</span>'
                      : '<span style="color: #999;">○ Inactive</span>'}
                        </div>
                        <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid ${mapStyle === 'dark' ? '#333' : '#eee'}; font-size: 11px; color: ${mapStyle === 'dark' ? '#aaa' : '#666'};">
                          Lat: ${center[1].toFixed(6)}<br/>
                          Lng: ${center[0].toFixed(6)}
                        </div>
                      </div>
                    </div>
                  `)
                  .addTo(mapRef.current!);
              });
            } else if (zone.type === 'polygon' && zone.polygon) {
              const sourceId = layerId;

              mapRef.current!.addSource(sourceId, {
                type: 'geojson',
                data: {
                  type: 'Feature',
                  properties: { name: zone.name, color: zoneColor },
                  geometry: zone.polygon as any
                }
              });

              mapRef.current!.addLayer({
                id: layerId,
                type: 'fill',
                source: sourceId,
                paint: { 'fill-color': zoneColor, 'fill-opacity': zone.isActive ? 0.15 : 0.05 }
              });

              mapRef.current!.addLayer({
                id: `${layerId}-border`,
                type: 'line',
                source: sourceId,
                paint: {
                  'line-color': zoneColor,
                  'line-width': zone.isActive ? 3 : 1.5,
                  'line-dasharray': zone.isActive ? [1, 0] : [2, 2],
                  'line-opacity': zone.isActive ? 0.9 : 0.5
                }
              });

              mapRef.current!.addLayer({
                id: labelLayerId,
                type: 'symbol',
                source: sourceId,
                layout: {
                  'text-field': `🛡️ ${zone.name}`,
                  'text-size': 13,
                  'text-anchor': 'center'
                },
                paint: {
                  'text-color': zoneColor,
                  'text-halo-color': mapStyle === 'dark' ? '#000000' : '#ffffff',
                  'text-halo-width': 2
                }
              });

              geofenceLayersRef.current.add(layerId);
              geofenceLayersRef.current.add(`${layerId}-border`);
              geofenceLayersRef.current.add(labelLayerId);
            }
          });
        } catch (error) {
          console.error(`Error fetching zones for vehicle ${vehicle.plateNumber}:`, error);
        }
      }

      setGeofenceZones(allZones);
      const totalZones = Object.values(allZones).reduce((sum, zones) => sum + zones.length, 0);
      console.log(`✅ Displayed ${totalZones} geofence zones for ${Object.keys(allZones).length} vehicles`);
    } catch (error) {
      console.error('Error displaying geofence zones:', error);
    }
  }, [mapLoaded, mapStyle]);

  useEffect(() => {
    if (mapLoaded && showGeofences) fetchAndDisplayGeofenceZones();
  }, [mapLoaded, showGeofences, fetchAndDisplayGeofenceZones]);

  useEffect(() => {
    if (!mapLoaded) return;
    const visibility = showGeofences ? 'visible' : 'none';
    geofenceLayersRef.current.forEach(layerId => {
      if (mapRef.current?.getLayer(layerId)) {
        mapRef.current.setLayoutProperty(layerId, 'visibility', visibility);
      }
    });
  }, [showGeofences, mapLoaded]);

  const createVehicleMarker = useCallback((device: Device, telemetry: any, isSelected: boolean) => {
  
    const getColor = () => {
      // Check if device is inactive (no data in 7+ days)
      const lastSeen = telemetry?.timestamp || telemetry?.processed_at;
      const isInactive = !lastSeen ||
        (Date.now() - new Date(lastSeen).getTime()) > 7 * 24 * 60 * 60 * 1000;
    
      if (isInactive) return '#9e9e9e'; // light grey for inactive
    
      if (colorMode === 'speed' && telemetry) {
        const speed = telemetry.speed || 0;
        if (speed > 80) return '#f44336';
        if (speed > 60) return '#ff9800';
        if (speed > 30) return '#fbc02d';
        return '#4caf50';
      }
      if (device.status === 'offline') return '#757575';
      return telemetry?.isMoving ? '#4caf50' : '#2196f3';
    };

    const color = getColor();
    const size = isSelected ? 56 : 40;
    const heading = telemetry?.heading || 0;

    const el = document.createElement('div');
    el.className = 'vehicle-marker';
    el.style.cursor = 'pointer';
    el.style.width = size + 'px';
    el.style.height = size + 'px';
    el.style.transformOrigin = 'center center';

    el.innerHTML = `
      <svg width="${size}" height="${size}" viewBox="0 0 60 60" style="filter: drop-shadow(0 4px 12px rgba(0,0,0,0.4));">
        <defs>
          <linearGradient id="carGrad-${device.imei}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:${color};stop-opacity:1" />
            <stop offset="100%" style="stop-color:${color};stop-opacity:0.7" />
          </linearGradient>
        </defs>
        
        <g transform="translate(30, 30) rotate(${heading})">
          <ellipse cx="0" cy="0" rx="10" ry="18" fill="url(#carGrad-${device.imei})" opacity="0.3"/>
          <path d="M -8,-16 L -8,16 Q -8,18 -6,18 L 6,18 Q 8,18 8,16 L 8,-16 Q 8,-18 6,-18 L -6,-18 Q -8,-18 -8,-16 Z" 
                fill="url(#carGrad-${device.imei})" 
                stroke="white" 
                stroke-width="1.5"/>
          <rect x="-6" y="-14" width="12" height="8" rx="2" 
                fill="rgba(135,206,250,0.5)" 
                stroke="white" 
                stroke-width="0.8"/>
          <rect x="-6" y="6" width="12" height="8" rx="2" 
                fill="rgba(135,206,250,0.5)" 
                stroke="white" 
                stroke-width="0.8"/>
          <g>
            <circle cx="-7" cy="-10" r="3" fill="#1a1a1a" stroke="#444" stroke-width="1"/>
            <circle cx="-7" cy="-10" r="1.5" fill="#666"/>
            <circle cx="7" cy="-10" r="3" fill="#1a1a1a" stroke="#444" stroke-width="1"/>
            <circle cx="7" cy="-10" r="1.5" fill="#666"/>
            <circle cx="-7" cy="10" r="3" fill="#1a1a1a" stroke="#444" stroke-width="1"/>
            <circle cx="-7" cy="10" r="1.5" fill="#666"/>
            <circle cx="7" cy="10" r="3" fill="#1a1a1a" stroke="#444" stroke-width="1"/>
            <circle cx="7" cy="10" r="1.5" fill="#666"/>
          </g>
          <rect x="-5" y="-2" width="10" height="4" rx="1" fill="rgba(255,255,255,0.2)"/>
          <circle cx="-4" cy="-17" r="1" fill="#fff" opacity="0.8"/>
          <circle cx="4" cy="-17" r="1" fill="#fff" opacity="0.8"/>
        </g>
        
        <path d="M30,-4 L26,1 L34,1 Z" fill="white" opacity="0.95" stroke="${color}" stroke-width="1.2"/>
        
        ${isSelected ? `
          <circle cx="30" cy="30" r="28" fill="none" stroke="${color}" stroke-width="2.5" opacity="0.6">
            <animate attributeName="r" values="24;34;24" dur="2s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.8;0.2;0.8" dur="2s" repeatCount="indefinite"/>
          </circle>
        ` : ''}
        
        ${telemetry?.isMoving ? `
          <circle cx="30" cy="30" r="32" fill="none" stroke="#4caf50" stroke-width="2" opacity="0.7">
            <animate attributeName="r" values="28;42;28" dur="1.5s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.9;0.1;0.9" dur="1.5s" repeatCount="indefinite"/>
          </circle>
        ` : ''}
      </svg>
    `;

    el.addEventListener('click', (e) => {
      e.stopPropagation();
      setSelectedDevice(device);
    });

    return el;
  }, [colorMode]);

  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;

    // ── NEW: update GeoJSON cluster source with current positions ────────────
    _updateClusterSource();

    console.log('Updating markers for', fleetDevices.size, 'devices');

    const devicesToShow = Array.from(fleetDevices.values())
      .filter(({ device }) => device.isBoundToVehicle)
      .filter(({ device }) => showOffline || device.status !== 'offline');

    Object.keys(markersRef.current).forEach(imei => {
      if (!fleetDevices.has(imei)) {
        markersRef.current[imei].remove();
        delete markersRef.current[imei];
      }
    });

    devicesToShow.forEach(({ device, telemetry }) => {
      let lng: number | undefined;
      let lat: number | undefined;

      if (telemetry?.location?.latitude && telemetry?.location?.longitude) {
        lng = telemetry.location.longitude;
        lat = telemetry.location.latitude;
      } else {
        const coords = device.location?.coordinates;
        if (coords && coords[0] !== 0 && coords[1] !== 0) {
          [lng, lat] = coords;
        }
      }

      if (!lng || !lat || lng === 0 || lat === 0) return;
      if (lng < -180 || lng > 180 || lat < -90 || lat > 90) return;

      const isSelected = selectedDevice?.imei === device.imei;

      if (markersRef.current[device.imei]) {
        const marker = markersRef.current[device.imei];
        marker.setLngLat([lng, lat]);
        const wasSelected = marker.getElement().dataset.selected === 'true';
        if (isSelected !== wasSelected) {
          const el = marker.getElement();
          const newEl = createVehicleMarker(device, telemetry, isSelected);
          el.innerHTML = newEl.innerHTML;
          el.dataset.selected = isSelected.toString();
        }
      } else {
        const el = createVehicleMarker(device, telemetry, isSelected);
        el.dataset.selected = isSelected.toString();

        const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat([lng, lat])
          .addTo(mapRef.current!);

        const isVehicleIdPopulated = device.vehicleId && typeof device.vehicleId === 'object';
        const vehicle = vehicleData[device.imei] || (isVehicleIdPopulated ? device.vehicleId : null);
        const displayName = vehicle?.plateNumber || device.plateNumber || 'Unknown Vehicle';
        const makeModel = vehicle ? `${vehicle.make} ${vehicle.model} (${vehicle.year})` :
          device.make && device.model ? `${device.make} ${device.model}` : 'Vehicle details unavailable';

        const speed = parseSpeed(telemetry);
        const displaySpeed = speed > 0 ? Math.round(speed) : lastKnownSpeeds[device.imei] || 0;

        const locationKey = `${lat.toFixed(4)},${lng.toFixed(4)}`;
        if (!addresses[locationKey] && lat && lng) reverseGeocode(lat, lng);
        const displayAddress = addresses[locationKey] || 'Loading address...';

        const popupContent = `
          <div style="padding: 12px; min-width: 0; max-width: 240px; overflow-wrap: anywhere; font-family: system-ui;">
            <div style="font-weight: bold; margin-bottom: 8px; color: #1976d2; font-size: 15px;">
              ${displayName}
            </div>
            <div style="font-size: 13px; color: #666; margin-bottom: 8px;">
              ${makeModel}
            </div>
            ${telemetry ? `
              <div style="font-size: 12px; margin-top: 10px; padding-top: 10px; border-top: 1px solid #eee;">
                <div style="margin-bottom: 4px;">
                  <strong>Speed:</strong> ${displaySpeed} km/h
                  ${speed > 0 ? '' : ' <span style="color: #999;">(last known)</span>'}
                </div>
                <div style="margin-bottom: 4px; color:fff">
                  <strong>Engine:</strong> ${telemetry.ignition ? '🟢 On' : '🔴 Off'}
                </div>
                <div style="margin-bottom: 8px;">
                  <strong>Location:</strong><br/>
                  <span style="font-size: 11px; color: #666;">${displayAddress}</span>
                </div>
                <div style="font-size: 11px; color: #999; margin-top: 8px; padding-top: 8px; border-top: 1px solid #eee;">
                  Last update: ${new Date(telemetry.timestamp).toLocaleString()}
                </div>
              </div>
            ` : '<div style="color: #999; font-size: 12px; margin-top: 8px;">No recent data</div>'}
          </div>
        `;

        marker.setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(popupContent));
        markersRef.current[device.imei] = marker;
      }
    });
  }, [fleetDevices, selectedDevice, createVehicleMarker, showOffline, mapLoaded, vehicleData,
    lastKnownSpeeds, addresses, reverseGeocode, _updateClusterSource]);

  useEffect(() => {
    if (!selectedDevice || !mapRef.current) return;

    const deviceData = fleetDevices.get(selectedDevice.imei);
    if (!deviceData) return;

    let lng: number | undefined;
    let lat: number | undefined;

    if (deviceData.telemetry?.location?.latitude && deviceData.telemetry?.location?.longitude) {
      lng = deviceData.telemetry.location.longitude;
      lat = deviceData.telemetry.location.latitude;
    } else if (deviceData.device.location?.coordinates) {
      const coords = deviceData.device.location.coordinates;
      if (coords[0] !== 0 && coords[1] !== 0) [lng, lat] = coords;
    }

    if (lng && lat) mapRef.current.flyTo({ center: [lng, lat], zoom: 15, duration: 1000 });
  }, [selectedDevice, fleetDevices]);

  const centerOnFleet = () => {
    if (!mapRef.current || fleetDevices.size === 0) return;

    const bounds = new mapboxgl.LngLatBounds();
    let hasValidCoords = false;

    fleetDevices.forEach(({ device, telemetry }) => {
      let lng: number | undefined;
      let lat: number | undefined;

      if (telemetry?.location?.latitude && telemetry?.location?.longitude) {
        lng = telemetry.location.longitude;
        lat = telemetry.location.latitude;
      } else if (device.location?.coordinates) {
        const coords = device.location.coordinates;
        if (coords[0] !== 0 && coords[1] !== 0) [lng, lat] = coords;
      }

      if (lng && lat) { bounds.extend([lng, lat]); hasValidCoords = true; }
    });

    if (hasValidCoords) mapRef.current.fitBounds(bounds, { padding: 80, duration: 1000 });
  };

  const toggleMapStyle = () => {
    if (!mapRef.current || !mapLoaded) return;

    const newStyle = mapStyle === 'dark' ? 'light' : 'dark';
    const styleUrl = newStyle === 'dark'
      ? 'mapbox://styles/mapbox/dark-v11'
      : 'mapbox://styles/mapbox/streets-v12';

    setMapLoaded(false);
    clusterSourceReadyRef.current = false;
    setMapStyle(newStyle);
    // Rebuild instead of diffing a style with partially initialized fog.
    mapRef.current.setStyle(styleUrl, { diff: false });

  };

  const totalZones = Object.values(geofenceZones).reduce((sum, zones) => sum + zones.length, 0);
  const activeZones = Object.values(geofenceZones).reduce((sum, zones) =>
    sum + zones.filter(z => z.isActive).length, 0
  );

  return (
    <Box sx={{ position: 'relative', height: { xs: 'calc(100dvh - 100px)', md: 'calc(100dvh - 130px)' }, minHeight: 500, width: '100%', bgcolor: '#000' }}>
      <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

      {!isConnected && (
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: mapStyle === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', zIndex: 9999 }}>
          <Paper elevation={8} sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6">Connecting to fleet tracker...</Typography>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
                <Button size="small" onClick={reconnect} sx={{ ml: 2 }}>Retry</Button>
              </Alert>
            )}
          </Paper>
        </Box>
      )}

      <Fade in={true}>
        <Paper elevation={8} sx={{ position: 'absolute', top: { xs: 8, sm: 16 }, left: { xs: 8, sm: 16 }, p: { xs: 1.25, sm: 2 }, bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', borderRadius: 3, minWidth: 0, width: { xs: 'calc(100% - 76px)', sm: 'auto' }, maxWidth: 360 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: isConnected ? 'success.main' : 'error.main', animation: isConnected && isSubscribed ? 'pulse 2s infinite' : 'none', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }} />
            <Typography variant="subtitle2" fontWeight="bold">
              Fleet Tracker {isConnected && isSubscribed ? 'Live' : 'Connecting...'}
            </Typography>
            {!isConnected && <IconButton size="small" onClick={reconnect}><Refresh fontSize="small" /></IconButton>}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: totalZones > 0 ? 1.5 : 0 }}>
            <Chip size="small" icon={<DirectionsCar />} label={`${stats.total} Total`} />
            <Chip size="small" color="success" label={`${stats.online} Online`} />
            <Chip size="small" color="warning" label={`${stats.moving} Moving`} />
            <Chip size="small" color="error" label={`${stats.offline} Offline`} />
          </Box>
          {totalZones > 0 && (
            <Box sx={{ pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Shield sx={{ color: '#3b82f6', fontSize: 18 }} />
                <Typography variant="caption" fontWeight="bold" color="#3b82f6">
                  Geofence Zones
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {activeZones} active • {totalZones} total
              </Typography>
            </Box>
          )}
        </Paper>
      </Fade>

      <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Tooltip title={`Switch to ${mapStyle === 'dark' ? 'light' : 'dark'} mode`} placement="left">
          <Paper elevation={4} sx={{ borderRadius: 2 }}>
            <IconButton onClick={toggleMapStyle} sx={{ bgcolor: mapStyle === 'dark' ? 'rgba(0,0,0,0.8)' : 'white', color: mapStyle === 'dark' ? 'white' : 'black', '&:hover': { bgcolor: mapStyle === 'dark' ? 'rgba(0,0,0,0.9)' : 'rgba(0,0,0,0.05)' } }}>
              {mapStyle === 'dark' ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Paper>
        </Tooltip>

        <Tooltip title="Center on fleet" placement="left">
          <Paper elevation={4} sx={{ borderRadius: 2 }}>
            <IconButton onClick={centerOnFleet} sx={{ bgcolor: mapStyle === 'dark' ? 'rgba(0,0,0,0.8)' : 'white', color: mapStyle === 'dark' ? 'white' : 'black', '&:hover': { bgcolor: mapStyle === 'dark' ? 'rgba(0,0,0,0.9)' : 'rgba(0,0,0,0.05)' } }}>
              <MyLocation />
            </IconButton>
          </Paper>
        </Tooltip>

        <Tooltip title="Color mode" placement="left">
          <Paper elevation={4} sx={{ borderRadius: 2 }}>
            <IconButton onClick={() => setColorMode(prev => prev === 'status' ? 'speed' : 'status')} sx={{ bgcolor: mapStyle === 'dark' ? 'rgba(0,0,0,0.8)' : 'white', color: mapStyle === 'dark' ? 'white' : 'black', '&:hover': { bgcolor: mapStyle === 'dark' ? 'rgba(0,0,0,0.9)' : 'rgba(0,0,0,0.05)' } }}>
              {colorMode === 'status' ? <Layers /> : <Speed />}
            </IconButton>
          </Paper>
        </Tooltip>

        <Tooltip title="Filters" placement="left">
          <Paper elevation={4} sx={{ borderRadius: 2 }}>
            <IconButton onClick={() => setFilterDrawerOpen(true)} sx={{ bgcolor: mapStyle === 'dark' ? 'rgba(0,0,0,0.8)' : 'white', color: mapStyle === 'dark' ? 'white' : 'black', '&:hover': { bgcolor: mapStyle === 'dark' ? 'rgba(0,0,0,0.9)' : 'rgba(0,0,0,0.05)' } }}>
              <FilterList />
            </IconButton>
          </Paper>
        </Tooltip>
      </Box>

      <Fade in={true}>
        <Paper elevation={4} sx={{ position: 'absolute', bottom: { xs: 32, sm: 80 }, left: { xs: 8, sm: 16 }, p: { xs: 1.25, sm: 2 }, maxHeight: { xs: 150, sm: 'none' }, overflowY: 'auto', bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', borderRadius: 2 }}>
          <Typography variant="caption" fontWeight="bold" display="block" mb={1}>
            {colorMode === 'status' ? 'Status' : 'Speed'}
          </Typography>
          {colorMode === 'status' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4caf50' }} /><Typography variant="caption">Moving</Typography></Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#2196f3' }} /><Typography variant="caption">Parked</Typography></Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#757575' }} /><Typography variant="caption">Offline</Typography></Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#9e9e9e' }} /><Typography variant="caption">Inactive (7d+)</Typography></Box>
              </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f44336' }} /><Typography variant="caption">&gt;80 km/h</Typography></Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff9800' }} /><Typography variant="caption">60-80 km/h</Typography></Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#fbc02d' }} /><Typography variant="caption">30-60 km/h</Typography></Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4caf50' }} /><Typography variant="caption">&lt;30 km/h</Typography></Box>
            </Box>
          )}
          {/* ── NEW: Cluster legend ── */}
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
            <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>Clusters</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4caf50' }} /><Typography variant="caption">1–4 vehicles</Typography></Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff9800' }} /><Typography variant="caption">5–9 vehicles</Typography></Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f44336' }} /><Typography variant="caption">10+ vehicles</Typography></Box>
            </Box>
          </Box>
        </Paper>
      </Fade>

      {selectedDevice && (
        <Fade in={true}>
          <Paper elevation={12} sx={{
            position: 'absolute', bottom: { xs: 28, sm: 16 }, right: { xs: 8, sm: 16 }, p: { xs: 2, sm: 3 }, zIndex: 2, width: { xs: 'calc(100% - 16px)', sm: 'auto' }, maxHeight: '60%', overflowY: 'auto',
            bgcolor: mapStyle === 'dark' ? 'rgba(30,30,30,0.98)' : 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(20px)',
            borderRadius: 3,
            minWidth: 0,
            maxWidth: { xs: 'none', sm: 420 },
            color: mapStyle === 'dark' ? '#fff' : '#000',
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                {(() => {
                  const isVehicleIdPopulated = selectedDevice.vehicleId && typeof selectedDevice.vehicleId === 'object';
                  const vehicle = vehicleData[selectedDevice.imei] || (isVehicleIdPopulated ? selectedDevice.vehicleId : null);
                  const plateNumber = vehicle?.plateNumber || selectedDevice.plateNumber || 'Unknown';
                  const make = vehicle?.make || selectedDevice.make || '';
                  const model = vehicle?.model || selectedDevice.model || '';
                  const year = vehicle?.year || selectedDevice.modelYear || '';

                  console.log('🔍 DEBUG INFO:', {
                    imei: selectedDevice.imei,
                    deviceIsBound: selectedDevice.isBoundToVehicle,
                    deviceVehicleId: selectedDevice.vehicleId,
                    vehicleDataExists: !!vehicle,
                    vehicleData: vehicle,
                    devicePlateNumber: selectedDevice.plateNumber,
                    deviceMake: selectedDevice.make,
                    deviceModel: selectedDevice.model,
                    allVehicleDataKeys: Object.keys(vehicleData)
                  });

                  return (
                    <>
                      <Typography variant="h5" fontWeight="bold" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <DirectionsCar sx={{ fontSize: 28, color: 'primary.main' }} />
                        {plateNumber}
                      </Typography>
                      {(make || model) && (
                        <Typography variant="body1" color="text.secondary">
                          {make} {model} {year && `(${year})`}
                        </Typography>
                      )}
                    </>
                  );
                })()}
              </Box>
              <IconButton size="small" onClick={() => setSelectedDevice(null)} sx={{ bgcolor: mapStyle === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', color: mapStyle === 'dark' ? '#fff' : 'inherit', '&:hover': { bgcolor: mapStyle === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)' } }}>
                <Close />
              </IconButton>
            </Box>

            <Box sx={{ mb: 2, p: 1.5, bgcolor: mapStyle === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', borderRadius: 1.5, borderLeft: '3px solid #1976d2' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ minWidth: 60 }}>VIN:</Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {(() => {
                      const vehicle = vehicleData[selectedDevice.imei];
                      console.log('═══════════════════════════════════════');
                      console.log('🔍 VEHICLE DATA DEBUG');
                      console.log('═══════════════════════════════════════');
                      console.log('Selected Device IMEI:', selectedDevice.imei);
                      console.log('Device is bound?', selectedDevice.isBoundToVehicle);
                      console.log('Device vehicleId:', selectedDevice.vehicleId);
                      console.log('Vehicle data exists?', !!vehicle);
                      console.log('Vehicle data:', vehicle);
                      console.log('-------------------');
                      console.log('Device Properties:');
                      console.log('  - plateNumber:', selectedDevice.plateNumber);
                      console.log('  - make:', selectedDevice.make);
                      console.log('  - model:', selectedDevice.model);
                      console.log('  - modelYear:', selectedDevice.modelYear);
                      console.log('  - vin:', selectedDevice.vin);
                      console.log('-------------------');
                      console.log('All vehicle data keys:', Object.keys(vehicleData));
                      console.log('All vehicles stored:', vehicleData);
                      console.log('═══════════════════════════════════════');
                      return vehicle?.vin || selectedDevice.vin || 'N/A';
                    })()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ minWidth: 60 }}>IMEI:</Typography>
                  <Typography variant="body2" fontFamily="monospace">{selectedDevice.imei}</Typography>
                </Box>
              </Box>
            </Box>

            {(() => {
              const deviceData = fleetDevices.get(selectedDevice.imei);
              const telemetry = deviceData?.telemetry;

              if (!telemetry) {
                return (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No recent telemetry data
                  </Typography>
                );
              }

              const ignitionOn = parseIgnition(telemetry);
              const currentSpeed = parseSpeed(telemetry);
              const lastSeen = new Date((telemetry as any).processed_at || telemetry.timestamp).getTime();
              const isDataStale = (Date.now() - lastSeen) > 15 * 60 * 1000;
              const isReallyMoving = !isDataStale && currentSpeed > 3;
              const displaySpeed = currentSpeed > 0 ? Math.round(currentSpeed) : lastKnownSpeeds[selectedDevice.imei] || 0;
              const isLastKnown = currentSpeed === 0;
              const { lat, lng } = parseLocation(telemetry);

              let displayAddress = 'No GPS coordinates yet';
              let locationKey: string | null = null;

              if (lat !== null && lng !== null) {
                locationKey = `${lat.toFixed(4)},${lng.toFixed(4)}`;
                if (!addresses[locationKey]) reverseGeocode(lat, lng);
                displayAddress = addresses[locationKey] || 'Loading address...';
              }

              return (
                <>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                    <Box sx={{ p: 2, bgcolor: displaySpeed > 80 ? 'rgba(244, 67, 54, 0.1)' : 'rgba(0,0,0,0.03)', borderRadius: 2, border: displaySpeed > 80 ? '2px solid #f44336' : 'none' }}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                        Speed {isLastKnown && <span style={{ fontStyle: 'italic' }}>(last known)</span>}
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color={displaySpeed > 80 ? 'error' : 'inherit'}>{displaySpeed}</Typography>
                      <Typography variant="caption">km/h</Typography>
                    </Box>
                    <Box sx={{ p: 2, bgcolor: mapStyle === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Battery</Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {(() => {
                          const v = telemetry.battery_voltage_V           // JTT808
                            ?? (telemetry.state?.reported?.['67'] ? telemetry.state.reported['67'] / 1000 : null)  // Teltonika
                            ?? telemetry.batteryVoltage                   // fallback
                            ?? null;
                          return (v && parseFloat(v) > 0) ? `${parseFloat(v).toFixed(2)}V` : 'N/A';
                        })()}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Engine</Typography>
                      <Chip size="medium" label={ignitionOn ? 'On' : 'Off'} color={ignitionOn ? 'success' : 'default'} icon={ignitionOn ? <Speed /> : undefined} sx={{ fontWeight: 'bold', bgcolor: ignitionOn ? undefined : mapStyle === 'dark' ? 'rgba(255,255,255,0.15)' : undefined, color: ignitionOn ? undefined : mapStyle === 'dark' ? '#fff' : undefined }} />
                      </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Status</Typography>
                      <Chip size="medium" label={isReallyMoving ? 'Moving' : 'Parked'} color={isReallyMoving ? 'warning' : 'info'} sx={{ fontWeight: 'bold' }} />
                    </Box>
                  </Box>

                  <Box sx={{ p: 1.5, bgcolor: mapStyle === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', borderRadius: 1.5, mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" fontWeight="bold" mb={0.5}>Location</Typography>
                    <Typography variant="body2" display="block" mb={1} sx={{ color: mapStyle === 'dark' ? '#ffffff' : '#1a1a1a', fontSize: '14px', fontWeight: 600, lineHeight: 1.4 }}>
                      {displayAddress}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" fontFamily="monospace">
                      {lat !== null && lng !== null ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" fontWeight="bold" mb={0.5} mt={1.5}>Last Update</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(telemetry.timestamp).toLocaleString('en-NG', { timeZone: 'Africa/Lagos', dateStyle: 'short', timeStyle: 'medium' })}
                    </Typography>
                  </Box>
                </>
              );
            })()}
          </Paper>
        </Fade>
      )}

      <Drawer anchor="right" open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)} PaperProps={{ sx: { width: 'min(300px, 90vw)', p: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Filters & Settings</Typography>
          <IconButton size="small" onClick={() => setFilterDrawerOpen(false)}><Close /></IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle2">Geofence Zones</Typography>
          <FormControlLabel
            control={<Switch checked={showGeofences} onChange={(e) => setShowGeofences(e.target.checked)} color="primary" />}
            label="Show Geofence Zones"
          />
          {totalZones > 0 && (
            <Box sx={{ p: 1.5, bgcolor: 'rgba(59, 130, 246, 0.1)', borderRadius: 2 }}>
              <Typography variant="caption" display="block" fontWeight="bold">{activeZones} active zones</Typography>
              <Typography variant="caption" display="block" color="text.secondary">{totalZones} total zones</Typography>
            </Box>
          )}
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Visibility</Typography>
          <ButtonGroup fullWidth>
            <Button variant={showOffline ? 'contained' : 'outlined'} onClick={() => setShowOffline(true)}>All Vehicles</Button>
            <Button variant={!showOffline ? 'contained' : 'outlined'} onClick={() => setShowOffline(false)}>Online Only</Button>
          </ButtonGroup>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Color By</Typography>
          <ButtonGroup fullWidth orientation="vertical">
            <Button variant={colorMode === 'status' ? 'contained' : 'outlined'} onClick={() => setColorMode('status')} startIcon={<Layers />}>Status</Button>
            <Button variant={colorMode === 'speed' ? 'contained' : 'outlined'} onClick={() => setColorMode('speed')} startIcon={<Speed />}>Speed</Button>
          </ButtonGroup>
        </Box>
      </Drawer>

      {error && (
        <Paper elevation={8} sx={{ position: 'absolute', top: 80, left: '50%', transform: 'translateX(-50%)', p: 2, bgcolor: 'error.main', color: 'white', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2">{error}</Typography>
          <IconButton size="small" onClick={clearError} sx={{ color: 'white' }}><Close /></IconButton>
        </Paper>
      )}
    </Box>
  );
};

export default MinimalFleetMap;